// libs
import React, { memo, useMemo } from 'react';

// styles
import { Container } from './styles';

// hooks
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';
import { getActiveSpecialTagType } from 'shared/layouts/DetailsPage/helpers';

// helpers
import { isSaleLive, getEpoch } from 'shared/helpers/saleHelper';

// defs
interface IActionContainerProps {
  children: React.ReactNode;
  isBottomSheet?: boolean;
  className?: string;
  specialTagData?: ProductDetail.ISpecialTag;
  isSaleParticipating?: boolean;
  onSale?: boolean;
  isOutOfStock?: boolean;
  cartSpecialTags?: ProductDetail.ISpecialTag;
}

function ActionContainer({
  children,
  isBottomSheet = false,
  className = '',
  isSaleParticipating,
  specialTagData,
  onSale,
  isOutOfStock,
  cartSpecialTags
}: IActionContainerProps) {

  const {
    enabled: saleExpEnabled,
    saleCommunication,
    specialTag,
    theme
  } = useRemoteConfig('saleExperience') || {};

  const gradientColors = theme?.bgGradient;

  const { enabled, endDate, pdp: { enabled: isPdpEnabled, mrpDpDiscount: { onSaleTag, specialTag: mrpSpecialTag },
    offersBestPrice: { onSaleTag: cartOnSaleTag, specialTag: cartSpecialTag }
  } } = saleCommunication || {};

  const now = Math.floor(Date.now() / 1000);
  const saleLive = isSaleLive(saleCommunication);

  const extendedDateEnabled = saleCommunication?.dateExtended > endDate;
  const pdpSaleEnabled = !isOutOfStock && isSaleParticipating && saleExpEnabled && saleCommunication.enabled && isPdpEnabled;
  const onSaleTagEnabled = pdpSaleEnabled && onSaleTag;

  const salePriceParticipatingEnabled = pdpSaleEnabled && onSaleTagEnabled && extendedDateEnabled && saleLive;

  const specialTagStart = getEpoch(specialTagData?.epochStart);
  const specialTagEnd = getEpoch(specialTagData?.epochEnd);

  const specialTagWithPriceParticipatingEnabled = pdpSaleEnabled && extendedDateEnabled && saleLive &&
    mrpSpecialTag && specialTag?.pdpEnabled &&
    (specialTagData && Object.keys(specialTagData).length > 0 && specialTagData?.type && ((specialTagStart < now && specialTagEnd > now) || (specialTagData && (!specialTagStart || !specialTagEnd))));

  const pdpCartSaleEnabled = !isOutOfStock && saleExpEnabled && !isSaleParticipating && onSale && enabled && isPdpEnabled && saleLive;

  const pdpSpecialTag = getActiveSpecialTagType(specialTagData, cartSpecialTags, isSaleParticipating, onSale);

  const isSpecialTagDataEmpty = !specialTagData || Object.keys(specialTagData).length === 0;
  const isCartSpecialTagDataValid = cartSpecialTags && Object.keys(cartSpecialTags).length > 0 && pdpSpecialTag && cartSpecialTags?.type && cartSpecialTags?.type !== null &&
    ((getEpoch(cartSpecialTags?.epochEnd) > now) || (!getEpoch(cartSpecialTags?.epochEnd)));

  const isSpecialTagDataValid = useMemo(() => {
    return Boolean(
      specialTagData &&
      Object.keys(specialTagData).length > 0 &&
      specialTagData?.type &&
      specialTagData?.type !== null &&
      ((getEpoch(specialTagData?.epochEnd) > now) || (!getEpoch(specialTagData?.epochEnd)))
    );
  }, [specialTagData, now]);

  const priceCartRule = saleExpEnabled && (isSaleParticipating && onSale) && enabled && isPdpEnabled && saleLive;
  const priceCartRuleEnabled = !isOutOfStock && priceCartRule && (onSaleTag || cartOnSaleTag);
  const specialTagConfig = (mrpSpecialTag || cartSpecialTag);
  const priceCartSpecialTagEnabled = !isOutOfStock && specialTagConfig && priceCartRule && isSaleParticipating && (isSpecialTagDataEmpty || !isSpecialTagDataValid) && isCartSpecialTagDataValid;

  const pdpSpecialTagText = specialTag?.tagConfig[pdpSpecialTag]?.text || '';
  const specialTagGradientColor = specialTag?.tagConfig[pdpSpecialTag]?.bgGradient || [];
  return (
    <Container
      isBottomSheet={isBottomSheet}
      id="pdp-bottom-cta-container"
      className={`actionContainer ${className}`}
      showBorder={true}
      specialTagGradientColor={specialTagGradientColor}
      gradientColors={gradientColors}
      saleCommunication={!pdpCartSaleEnabled && !priceCartSpecialTagEnabled && (salePriceParticipatingEnabled || priceCartRuleEnabled)}
      specialTag={!pdpCartSaleEnabled && pdpSpecialTagText && (specialTagWithPriceParticipatingEnabled)}
    >
      {children}
    </Container>
  );
}

export default memo(ActionContainer);
